/**
 * @coeldery/variant-picker — 版本選擇器（**同一份歌詞，幾個演繹版本**）
 *
 * 為何要模組：AI 生成（歌／圖／文案）成日一次回幾個候選，用戶想**聽／睇完先揀** ——
 *   呢個模式第二個 app 都會用 → 依 R3 做模組。
 *   產品背景：Mureka 一次生成回 2 個版本（已收費）→ 唔准丟棄（`decisions/012`／`0004_song_variants.sql`）。
 *
 * 規格（**1.1.0；2026-09-23 產品負責人**：「should tap and change the color instead of having a button
 *   saying using this… and both version should add the sharing button here」）：
 *   · 每個版本 ＝ 一張**卡**：**撳卡（label 行）＝揀呢個版本** → 卡即刻變色（`aria-pressed`）
 *     ⚠️ 冇咗獨立嘅「用呢個」掣（之前要另外撳一粒掣 → 產品負責人話唔直覺）
 *   · 每個版本有自己嘅 `<audio controls>` 可以 A/B 試聽（**撳 audio 唔會當揀版本**）
 *   · ⭐ 每個版本可以**注入額外動作**（`renderActions`）—— 例：逐個版本分享（父層注入 `ShareButton`，
 *     模組本身**唔識**分享邏輯，亦唔准做網絡請求）
 *   · `busy` 時唔准入（防重複送）；文字一律 i18n key；顏色只用 CSS var
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export interface SongVariant {
  index: number;
  durationMs: number | null;
  hasHifi: boolean;
}

export interface VariantPickerProps {
  variants: SongVariant[];
  pickedIndex: number;
  busy?: boolean;
  onPick: (index: number) => void;
  /** 每個版本嘅音頻 URL（父層提供 —— 要帶認證／sessionId） */
  audioSrcFor: (index: number) => string;
  /** ⭐ 1.1.0：每個版本嘅額外動作（例：分享）；由父層注入（模組唔識產品內容） */
  renderActions?: (variant: SongVariant) => ReactNode;
}

/** 毫秒 → `m:ss`（唔用 library；顯示用） */
function mmss(ms: number | null): string {
  if (!ms || ms <= 0) return '';
  const total = Math.round(ms / 1000);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

export default function VariantPicker({
  variants,
  pickedIndex,
  busy = false,
  onPick,
  audioSrcFor,
  renderActions,
}: VariantPickerProps) {
  const { t } = useTranslation();
  if (variants.length < 2) return null; // 只有一個版本 → 冇嘢好揀

  return (
    <section
      aria-label={t('variant_label')}
      data-testid="variant-picker"
      style={{
        margin: '0 12px 16px',
        padding: '14px',
        borderRadius: '18px',
        background: 'var(--color-card)',
        border: '1px solid var(--color-divider)',
        boxShadow: 'var(--shadow-soft, none)',
      }}
    >
      <h3 style={{ margin: '0 0 10px', fontSize: 'var(--font-size-body, 18px)', color: 'var(--color-text)' }}>{t('variant_label')}</h3>
      <p className="footnote" style={{ margin: '0 0 10px', color: 'var(--color-text-secondary)' }}>
        {t('variant_tap_hint')}
      </p>
      <div style={{ display: 'grid', gap: '12px' }}>
        {variants.map((v, i) => {
          const picked = v.index === pickedIndex;
          const length = mmss(v.durationMs);
          return (
            <div
              key={v.index}
              data-testid={`variant-card-${i}`}
              role="group"
              /** 撳卡（任何非互動位置）＝揀呢個版本 —— 產品負責人要求「tap and change the color」 */
              onClick={busy || picked ? undefined : () => onPick(v.index)}
              style={{
                padding: '10px 12px',
                borderRadius: '14px',
                border: `1.5px solid ${picked ? 'var(--color-primary)' : 'var(--color-divider)'}`,
                background: picked ? 'var(--color-primary-soft, var(--color-card))' : 'transparent',
                cursor: busy || picked ? 'default' : 'pointer',
              }}
            >
              <button
                type="button"
                data-testid={`variant-${i}`}
                aria-pressed={picked}
                disabled={busy || picked}
                onClick={(e) => {
                  /** 唔准兩次觸發（卡 onClick 已經處理） */
                  e.stopPropagation();
                  onPick(v.index);
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  margin: '0 0 8px',
                  padding: 0,
                  border: 'none',
                  background: 'transparent',
                  fontFamily: 'inherit',
                  fontSize: 'var(--font-size-small, 14px)',
                  fontWeight: 700,
                  color: picked ? 'var(--color-primary)' : 'var(--color-text)',
                  cursor: busy || picked ? 'default' : 'pointer',
                }}
              >
                {t('variant_label')} {i + 1}
                {length ? ` · ${length}` : ''}
                {v.hasHifi ? ` · ${t('variant_hifi')}` : ''}
                {picked ? ` · ${t('variant_picked')}` : ''}
              </button>
              {/* 撳 audio 唔准當揀版本（避免想試聽就揀咗） */}
              <div onClick={(e) => e.stopPropagation()}>
                {/* eslint-disable-next-line jsx-a11y/media-has-caption -- 音頻冇字幕概念（純音樂） */}
                <audio controls preload="none" src={audioSrcFor(v.index)} style={{ width: '100%' }} />
                {renderActions ? <div style={{ marginTop: '8px' }}>{renderActions(v)}</div> : null}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

